import { useTraduccion } from '@tallerya/i18n';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { api, direccionApi } from '../api';
import { useTaller } from '../taller/ProveedorTaller';

// Comprueba que el sitio y la API se hablan, y **que el contrato generado es el
// que la API está sirviendo**.
//
// ## Por qué existe esta pantallita
//
// F-4 dice que cambiar un DTO del backend y regenerar tiene que romper la
// compilación del frontend. Eso solo pasa si alguien **usa** los tipos
// generados: un paquete que nadie importa compila igual aunque el contrato
// cambie entero. Esta es esa primera persona que lo usa.
//
// El día que `tenantId` se llame de otro modo, `data.tenantId` de más abajo
// deja de compilar. Ese es todo el mecanismo, y por eso conviene que no
// desaparezca cuando lleguen las pantallas de verdad.
//
// ## Qué se espera ver
//
// Sin sesión, un 401: la API pide credenciales y hace bien. Eso ya comprueba
// tres cosas que fallan seguido y en silencio: que la dirección es la correcta,
// que CORS deja pasar, y que la política de «cerrado por omisión» está puesta.
// Con sesión, el taller de quien entró.

type Estado =
  | { tipo: 'consultando' }
  | { tipo: 'sinSesion' }
  | { tipo: 'conSesion'; taller: string }
  | { tipo: 'sinApi' };

export function EstadoApi() {
  const { t } = useTraduccion();
  const auth = useAuth();
  const { talleres } = useTaller();
  const [estado, setEstado] = useState<Estado>({ tipo: 'consultando' });

  // **Se espera a que la sesión esté resuelta antes de preguntar.**
  //
  // Sin esto, el efecto corría al montar —antes de que oidc-client terminara de
  // leer `sessionStorage` y de renovar el token—, la petición salía sin
  // credenciales y la API respondía 401. Se veía clarísimo en la pestaña de red:
  // `whoami` en 401 **por encima** de `openid-configuration` y de `token`, o sea
  // preguntando antes de tener con qué.
  //
  // El resultado engañaba: la pantalla decía «la API pide credenciales» con una
  // sesión perfectamente válida en la cabecera.
  //
  // `usePermisos` nunca tuvo el problema porque TanStack Query lo resuelve con
  // `enabled`. Acá, que es un efecto a mano, hay que escribirlo.
  const listo = !auth.isLoading;
  const autenticado = auth.isAuthenticated;

  useEffect(() => {
    if (!listo) return;

    // Si el componente se desmonta antes de que responda la API —cambiar de ruta
    // rápido, o el doble montaje de StrictMode en desarrollo— no se toca el
    // estado de algo que ya no está en pantalla.
    let vigente = true;

    async function consultar() {
      try {
        const { data, error } = await api.GET('/api/diagnostics/whoami');

        if (!vigente) return;

        if (error || !data) {
          setEstado({ tipo: 'sinSesion' });
          return;
        }

        // `tenantId` sale del contrato generado. Si el backend lo renombra y
        // alguien regenera, esta línea deja de compilar — que es el punto. Es el
        // taller que la API resolvió para esta petición (#205).
        setEstado({ tipo: 'conSesion', taller: data.tenantId ?? '—' });
      } catch {
        // Un fallo de red, no una respuesta: la API no está levantada, o CORS la
        // bloqueó. Se distingue del 401 a propósito, porque se arreglan distinto.
        if (vigente) setEstado({ tipo: 'sinApi' });
      }
    }

    void consultar();

    return () => {
      vigente = false;
    };

    // `autenticado` está en las dependencias para que entrar o salir vuelva a
    // preguntar. Sin él, quien entra ve «pide credenciales» hasta recargar.
  }, [listo, autenticado]);

  return (
    <section className="aviso">
      <h2>{t('admin.api.titulo')}</h2>
      <p>
        {estado.tipo === 'consultando' && t('admin.api.consultando')}
        {estado.tipo === 'sinSesion' && t('admin.api.sinSesion')}
        {/* Con su nombre, que es lo que se reconoce; el id solo si no está en la
            lista, que sería justo lo que hay que investigar. */}
        {estado.tipo === 'conSesion' &&
          t('admin.api.conSesion', {
            taller: talleres.find((taller) => taller.id === estado.taller)?.name ?? estado.taller,
          })}
        {estado.tipo === 'sinApi' && t('admin.api.sinApi', { direccion: direccionApi })}
      </p>
    </section>
  );
}
